import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import Table from "../items";
import NotFoundPage from "@/Pages/NotFoundPage";

import { Route, Switch, useLocation } from "wouter";
import { ItemsFormPage } from "@/Pages/ItemsFormPage";
import { SupplierForm } from "@/Pages/SupplierFormPage";
import DocumentView from "@/components/document";
import DocumentWithDetailsView from "@/components/document-details";

import Team from "@/components/team";
import ApiDocsPage from "@/components/api";
import UnitCodeMapper from "@/components/uom";
import InvoiceTable from "@/components/table-document";
import { ItemList } from "@/Pages/ItemList";
import { BuyerList } from "@/Pages/BuyerList";
import SupplierList from "@/Pages/SupplierList";
import { JobList } from "@/Pages/JobList";
import CompanyProfile from "@/Pages/CompanyProfile";
import InvoicePrefixNumber from "@/components/prefix-number";
import { Fragment } from "react/jsx-runtime";
import { useEffect } from "react";
import { Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/contexts/ThemeContext";
import Branch from "@/components/branch";
import ReportPage from "@/components/report";
import ImportPage from "@/Pages/ImportPage";
import ImportListPage from "@/Pages/ImportListPage";
import ImportDetailsPage from "@/Pages/ImportDetailsPage";
import MappingProfilesListPage from "@/Pages/MappingProfilesListPage";
import CreateMappingProfilePage from "@/Pages/CreateMappingProfilePage";
import MappingProfileDetailPage from "@/Pages/MappingProfileDetailPage";
import TestMappingPage from "@/Pages/TestMappingPage";
import EditMappingProfilePage from "@/Pages/EditMappingProfilePage";
import UnitOfMeasureRetired from "@/Pages/UnitOfMeasureRetired";
import DownloadedSales from "@/Pages/DownloadedSales";
import DownloadedPurchases from "@/Pages/DownloadedPurchases";
import { StockIn } from "@/Pages/StockIn";
import { StockOut } from "@/Pages/StockOut";
import { StockTransactionList } from "@/Pages/StockTransactionList";
import { StockHistory } from "@/Pages/StockHistory";
import { StockInList } from "@/Pages/StockInList";
import { StockOutList } from "@/Pages/StockOutList";
import { StockValuationReport } from "@/Pages/StockValuationReport";
import { StockInDetail } from "@/Pages/StockInDetail";
import { StockOutDetail } from "@/Pages/StockOutDetail";
import { StockInEdit } from "@/Pages/StockInEdit";
import { StockOutEdit } from "@/Pages/StockOutEdit";
import { StockAdjustment } from "@/Pages/StockAdjustment";
import MonthEndStockList from "@/Pages/MonthEndStockList";
import MonthEndStockAdd from "@/Pages/MonthEndStockAdd";
import MonthEndStockDetail from "@/Pages/MonthEndStockDetail";
import Quotations from "@/Pages/Quotations";
import { QuotationForm } from "@/Pages/QuotationForm";
import SalesOrders from "@/Pages/SalesOrders";
import { SalesOrderForm } from "@/Pages/SalesOrderForm";
import DeliveryOrders from "@/Pages/DeliveryOrders";
import { DeliveryOrderForm } from "@/Pages/DeliveryOrderForm";
import SalesDocuments from "@/Pages/SalesDocuments";
import GmailSettings from "@/Pages/GmailSettings";
import EmailInbox from "@/Pages/EmailInbox";
import EmailQuotationReview from "@/Pages/EmailQuotationReview";
import { useEmailInboxStore } from "@/store/emailInboxStore";

export default function Page() {
  const [location] = useLocation();
  const segments = location.split("/").filter(Boolean);
  const { theme, toggleTheme } = useTheme();
  const clearEmailSession = useEmailInboxStore((s) => s.clearAll);

  useEffect(() => {
    const isEmailPage =
      location.startsWith("/email-inbox") ||
      location.startsWith("/gmail-settings") ||
      location.startsWith("/email-quotation");
    if (!isEmailPage) {
      clearEmailSession();
    }
  }, [location]);

  // NOTE: A 60-second client-side reconciliation loop used to live here.

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 justify-between">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                {segments.map((segment, index) => {
                  const isLast =
                    segments.indexOf(segment) === segments.length - 1;

                  return (
                    <Fragment key={`${segment}-${index}`}>
                      <BreadcrumbItem
                        key={segment}
                        className="hidden md:block capitalize"
                      >
                        {isLast ? (
                          <BreadcrumbPage>{segment}</BreadcrumbPage>
                        ) : (
                          <BreadcrumbLink
                            href={`${["invoice", "invoice-details"].includes(segment) ? `/invoices` : `/${segments.slice(0, index + 1).join("/")}`}`}
                          >
                            {segment}
                          </BreadcrumbLink>
                        )}
                      </BreadcrumbItem>
                      {!isLast && (
                        <BreadcrumbSeparator className="hidden md:block" />
                      )}
                    </Fragment>
                  );
                })}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <div className="px-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label="Toggle dark mode"
            >
              {theme === "dark" ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </Button>
          </div>
        </header>
        <div className="flex flex-1 flex-col gap-4 p-8 md:p-4 pt-0">
          <Switch>
            <Route path="/" component={InvoiceTable} />

            <Route path="/invoices" component={InvoiceTable} />
            <Route path="/invoice/:documentType">
              <DocumentView />
            </Route>
            <Route path="/invoice-details/:documentType">
              <DocumentWithDetailsView />
            </Route>

            <Route path="/items" component={ItemList} />
            <Route path="/buyers" component={BuyerList} />
            <Route path="/suppliers" component={SupplierList} />
            <Route path="/job-codes" component={JobList} />
            {/* Retired  */}
            <Route path="/unitOfMeasure" component={UnitOfMeasureRetired} />
            <Route path="/team" component={Team} />
            <Route path="/api" component={ApiDocsPage} />
            <Route path="/uom" component={UnitCodeMapper} />
            <Route path="/prefix" component={InvoicePrefixNumber} />
            <Route path="/branch" component={Branch} />
            <Route path="/item/register" component={ItemsFormPage} />
            <Route path="/table" component={Table} />
            <Route path="/supplier/register" component={SupplierForm} />
            <Route path="/company" component={CompanyProfile} />
            <Route path="/report" component={ReportPage} />
            <Route path="/import" component={ImportPage} />
            <Route path="/import/records" component={ImportListPage} />
            <Route path="/import/records/:id" component={ImportDetailsPage} />
            <Route
              path="/mapping-profiles"
              component={MappingProfilesListPage}
            />
            <Route
              path="/mapping-profiles/create"
              component={CreateMappingProfilePage}
            />
            <Route
              path="/mapping-profiles/:id"
              component={MappingProfileDetailPage}
            />
            <Route
              path="/mapping-profiles/:id/edit"
              component={EditMappingProfilePage}
            />
            <Route
              path="/mapping-profiles/:id/test"
              component={TestMappingPage}
            />
            <Route path="/downloaded-e-invoices" component={DownloadedSales} />
            <Route
              path="/downloaded-e-invoices/Sales"
              component={DownloadedSales}
            />
            <Route
              path="/downloaded-e-invoices/Purchases"
              component={DownloadedPurchases}
            />

            <Route
              path="/stock-transactions"
              component={StockTransactionList}
            />
            <Route path="/stock-in" component={StockInList} />
            <Route path="/stock-in/add" component={StockIn} />
            <Route path="/stock-in/view" component={StockInDetail} />
            <Route path="/stock-in/edit" component={StockInEdit} />
            <Route path="/stock-out" component={StockOutList} />
            <Route path="/stock-out/add" component={StockOut} />
            <Route path="/stock-out/view" component={StockOutDetail} />
            <Route path="/stock-out/edit" component={StockOutEdit} />
            <Route path="/stock-history" component={StockHistory} />
            <Route path="/stock-valuation" component={StockValuationReport} />
            <Route path="/stock-adjustment" component={StockAdjustment} />
            <Route path="/month-end-stock" component={MonthEndStockList} />
            <Route path="/month-end-stock/add" component={MonthEndStockAdd} />
            <Route
              path="/month-end-stock/view/:id"
              component={MonthEndStockDetail}
            />

            <Route path="/sales-documents" component={SalesDocuments} />
            <Route path="/quotations" component={Quotations} />
            <Route path="/quotation/new" component={QuotationForm} />
            <Route path="/quotation/view" component={QuotationForm} />

            {/* Gmail AI Quotation Routes */}
            <Route path="/gmail-settings" component={GmailSettings} />
            <Route path="/email-inbox" component={EmailInbox} />
            <Route
              path="/email-quotation/:id"
              component={EmailQuotationReview}
            />

            <Route path="/sales-orders" component={SalesOrders} />
            <Route path="/sales-order/new" component={SalesOrderForm} />
            <Route path="/sales-order/view" component={SalesOrderForm} />

            <Route path="/delivery-orders" component={DeliveryOrders} />
            <Route path="/delivery-order/new" component={DeliveryOrderForm} />
            <Route path="/delivery-order/view" component={DeliveryOrderForm} />

            <Route>
              <NotFoundPage />
            </Route>
          </Switch>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
